import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import { SITE_NAME } from "@/config/site";
import { webPageSchema, breadcrumbListSchema } from "@/lib/schema";

export default function CategoryPage() {
  const { slug = "rexue" } = useParams();
  const navigate = useNavigate();

  const active = useMemo(
    () => categories.find((c) => c.slug === slug) ?? categories[0],
    [slug]
  );
  const list = useMemo(() => getArticlesByCategory(active.slug), [active.slug]);

  const title = `${active.name}精选推荐大全`;
  const description = `${active.desc} 精选${active.name}神作，榜单持续更新。`;

  useSeo({
    title,
    description,
    keywords: `日漫,${active.name},${active.name}推荐,日漫神作`,
    path: `/category/${active.slug}`,
    jsonLd: [
      webPageSchema(title, `/category/${active.slug}`, description),
      breadcrumbListSchema([
        { name: "首页", path: "/" },
        { name: active.name, path: `/category/${active.slug}` },
      ]),
    ],
  });

  const switchCategory = (next: string) => {
    if (next === active.slug) return;
    window.scrollTo({ top: 0, behavior: "auto" });
    navigate(`/category/${next}`);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Banner */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={active.banner}
              alt={`${active.name}背景`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/30" />
          </div>
          <div className="relative mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: active.name }]}
            />
            <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-4xl">
              {active.name}精选推荐
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-background-100/90 md:text-base">
              {active.desc}
            </p>
          </div>
        </section>

        {/* Category switcher */}
        <section className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="题材分类切换">
            {categories.map((c) => {
              const selected = c.slug === active.slug;
              return (
                <button
                  key={c.slug}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => switchCategory(c.slug)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    selected
                      ? "bg-primary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {c.name}
                  <span className={`ml-1.5 ${selected ? "text-background-50/80" : "text-secondary-700"}`}>
                    {getArticlesByCategory(c.slug).length}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Article grid */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-6 md:pb-20">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              共 {list.length} 部{active.name}神作
            </h2>
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
              {list.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-background-200 bg-background-100 p-10 text-center text-sm text-foreground-600">
              该分类正在整理中，敬请期待更多神作推荐。
            </p>
          )}
        </section>

        {/* Cross-link */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              探索更多题材
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories
                .filter((c) => c.slug !== active.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="whitespace-nowrap rounded-md bg-background-50 px-3.5 py-2 text-sm text-foreground-700 ring-1 ring-background-200 transition-colors hover:text-primary-700"
                  >
                    {c.name}
                  </Link>
                ))}
              <Link
                to="/about"
                className="whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium text-primary-700"
              >
                了解 {SITE_NAME}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}